import { AccessMethodType } from './property-access.model';
import { BookingPropertyModel } from './property.model';

/**
 * Mirrors guest-genie-backend BookingInfo (property-manager BookingExternalDetailsDto).
 */

export interface BookingGuestGenieGuestModel {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  guestGenieUserId?: string;
  preferences?: Preferences;
}

/** @deprecated Alias — use BookingGuestGenieGuestModel */
export type BookingGuestModel = BookingGuestGenieGuestModel;

export interface BookingGuestIntegrationDataModel {
  phone?: string;
  email?: string;
  externalId?: string;
  firstName?: string;
  lastName?: string;
}

export interface BookingGuestInfoModel {
  integrationData?: BookingGuestIntegrationDataModel;
  guestGenieGuests?: BookingGuestGenieGuestModel[];
}

export interface BookingInvitedGuestModel {
  email: string;
  firstName?: string;
  lastName?: string;
  externalId?: string;
  preferences?: Preferences;
  /** Not from bookings API; optional UI hint when invite was accepted. */
  verified?: boolean;
}

export interface BookingGuestVerificationModel {
  _id?: string;
  firebaseId?: string;
  guestId: string;
  status: BookingVerificationStatus | string;
  otpChannel?: OtpChannel | string;
  phoneId?: string;
  emailId?: string;
  createdAt?: string;
  lastSentAt?: string;
  verifiedByAdmin?: boolean;
  verifiedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
}

export interface BookingAccessMethodData {
  type: AccessMethodType;
  code?: string;
  image?: string;
  link?: string;
}

export interface BookingAccessMethod {
  type: AccessMethodType;
  data: BookingAccessMethodData;
}

/** Per-booking override for a single PER_BOOKING zone (matched to the property zone by key). */
export interface BookingAccessZone {
  key: string;
  type?: string;
  name?: string;
  order?: number;
  method?: BookingAccessMethod;
}

export interface BookingAccess {
  /** New multi-zone per-booking overrides. */
  zones?: BookingAccessZone[];
  /** Legacy single method (mirrors the first per-booking zone) — backward compatibility. */
  method?: BookingAccessMethod;
  enabled: boolean;
}

/** The per-booking override data (code/image/link) for a given zone key. */
export function getBookingZoneData(
  access: BookingAccess | undefined,
  key: string,
): BookingAccessMethodData | undefined {
  const zone = access?.zones?.find((z) => z.key === key);
  return zone?.method?.data;
}

export interface BookingPaidModel {
  total: number;
  currency: string;
  perNight: number;
  paymentStatus?: string;
  totalPaid?: number;
}

export interface BookingIntegrationModel {
  propertyId: string;
  pmsIntegration: string;
  bookingId: string;
  status: string;
  confirmationCode?: string;
  source?: string;
}

export interface BookingCheckInOutModel {
  date: string;
  serviceRequest: string;
  status: string;
}

export interface GuestQuantityModel {
  adults: number;
  children: number;
  infants: number;
  pets: number;
}

export interface BookingModel {
  _id: string;
  start: string;
  end: string;
  numberOfGuests: GuestQuantityModel;
  property: BookingPropertyModel | null;
  access?: BookingAccess;
  tags?: string[];
  stayType?: string;
  invitedGuests?: BookingInvitedGuestModel[];
  guest: BookingGuestInfoModel;
  guestVerifications?: BookingGuestVerificationModel[];
  paid?: BookingPaidModel;
  integration?: BookingIntegrationModel;
  earlyCheckIn?: BookingCheckInOutModel;
  lateCheckOut?: BookingCheckInOutModel;
  guestCanValidateIdentity?: boolean;
}

export interface Preferences {
  language?: string;
}

export interface BookingPostModel {
  propertyId: string;
  booking: {
    start?: Date;
    end?: Date;
    numberOfGuests: {
      adults: number;
      children: number;
      infants: number;
      pets: number;
    };
    stayType: StayTypeSelect;
  };
}

export interface MaskedPhoneModel {
  _id: string;
  maskedPhone: string;
}

export interface MaskedEmailModel {
  _id: string;
  maskedEmail: string;
}

export interface AvailableUserVerificationChannelsModel {
  enabledChannels: OtpChannel[];
  phones?: MaskedPhoneModel[];
  emails?: MaskedEmailModel[];
}

/**
 * Returned by GET /v1/properties/:idOrCode/booking-entry-context.
 */
export interface BookingEntryContextModel {
  hasCandidate: boolean;
  bookingVerificationToken?: string;
  maskedPhone?: string;
  enabledChannels?: OtpChannel[];
}

export enum OtpChannel {
  SMS = 'sms',
  EMAIL = 'email',
  WHATSAPP = 'whatsapp',
}

export enum StayType {
  LONG_TERM = 'long-term',
  SHORT_TERM = 'short-term',
  MID_TERM = 'mid-term',
  RESIDENTIAL = 'residential',
}

export enum StayTypeSelect {
  VACATION = 'vacation',
  RESIDENTIAL = 'residential',
}

export enum BookingVerificationStatus {
  PENDING = 'pending',
  VERIFIED = 'verified',
  REVOKED = 'revoked',
  ERROR = 'error',
}
